/** Regenerate-as-new fork helpers — explicit payload, upstream edge copy,
 * prompt mention remapping. Never shallow-clone stale/run/output state. */

import type { Edge } from '@xyflow/react'
import { makeMentionToken, parsePromptText } from './assetMention'
import { stripGenerationExecutionFields } from './canvasGenerationExecutionStrip'
import { imageReferenceAssetHandleId, isImageReferenceAssetHandle } from './dataflow'

export const stripRegenerateForkOutputFields = stripGenerationExecutionFields

export function remapMentionEdgeIds(text: string, edgeIdMap: Map<string, string>): string {
  if (!text || edgeIdMap.size === 0) return text
  return parsePromptText(text)
    .map((seg) => {
      if (seg.type === 'text') return seg.value
      const mapped = edgeIdMap.get(seg.edgeId) ?? seg.edgeId
      return makeMentionToken(mapped, seg.aliasHint)
    })
    .join('')
}

export function remapForkPromptFields(
  nodeType: string,
  fields: Record<string, unknown>,
  edgeIdMap: Map<string, string>,
): Record<string, unknown> {
  if (edgeIdMap.size === 0) return fields
  if (nodeType === 'generate_video' && typeof fields.promptText === 'string') {
    return { ...fields, promptText: remapMentionEdgeIds(fields.promptText, edgeIdMap) }
  }
  if (nodeType === 'generate_image' && typeof fields.prompt === 'string') {
    return { ...fields, prompt: remapMentionEdgeIds(fields.prompt, edgeIdMap) }
  }
  if (nodeType === 'generate_text' && typeof fields.additionalInstruction === 'string') {
    return { ...fields, additionalInstruction: remapMentionEdgeIds(fields.additionalInstruction, edgeIdMap) }
  }
  return fields
}

export interface ForkIncomingEdgesResult {
  copiedEdges: Edge[]
  edgeIdMap: Map<string, string>
}

/** Copy every canonical incoming edge onto `targetNodeId` (siblings, not B→C). */
export function copyIncomingEdgesForFork(
  edges: Edge[],
  sourceNodeId: string,
  targetNodeId: string,
  newEdgeId: () => string,
): ForkIncomingEdgesResult {
  const incoming = edges.filter((e) => e.target === sourceNodeId)
  const edgeIdMap = new Map<string, string>()
  const copiedEdges = incoming.map((edge) => {
    const id = newEdgeId()
    edgeIdMap.set(edge.id, id)
    // VistoeraTASK-00144 — a reference-image edge's target handle is
    // scoped to its OWN edge id (`imageReferenceAssetHandleId`); a plain
    // `{ ...edge, id }` copy would keep pointing at the ORIGIN edge's
    // now-stale suffix, leaving the forked sibling's Related Assets with
    // no matching DOM handle. Re-scope to the fork's new id; every other
    // handle kind (keyframe/structured-asset/prompt/etc.) is untouched.
    const targetHandle = isImageReferenceAssetHandle(edge.targetHandle)
      ? imageReferenceAssetHandleId(id)
      : edge.targetHandle
    return { ...edge, id, target: targetNodeId, targetHandle }
  })
  return { copiedEdges, edgeIdMap }
}

export function buildRegenerateForkFields(
  nodeType: string,
  originFields: Record<string, unknown> | undefined,
  edgeIdMap: Map<string, string>,
): Record<string, unknown> {
  const stripped = stripRegenerateForkOutputFields(originFields || {})
  const remapped = remapForkPromptFields(nodeType, stripped, edgeIdMap)
  return { ...remapped, status: 'READY', pendingAutoGenerate: true }
}
